import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ConsentToggleRow } from "../../components/ConsentToggleRow";
import { formatDate, formatDateTime } from "../../lib/dateUtils";

const PAIRING_OPTIONS = ["any", "same_gender", "opposite_gender", "women_only", "men_only"];
const INTERESTS = ["Hiking", "Photography", "Music", "Travel", "Coffee", "Art", "Fitness", "Reading", "Food", "Nature"];
const POSING_KEYS = [
  { key: "hand_holding", label: "Hand holding" },
  { key: "hugging", label: "Hugging" },
  { key: "sitting_close", label: "Sitting close" },
  { key: "arm_around_shoulder", label: "Arm around shoulder" },
  { key: "other", label: "Other" },
];

export default function StrangerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/strangers/${id}`).then(({ data }) => setData(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startEditing = () => {
    setForm({
      ...data.stranger,
      interests: data.stranger.interests || [],
      pairing_comfort: data.stranger.pairing_comfort || [],
      posing_boundaries: data.stranger.posing_boundaries || {},
    });
    setEditing(true);
  };

  const toggleListValue = (field, value) => {
    setForm((f) => ({ ...f, [field]: f[field].includes(value) ? f[field].filter((v) => v !== value) : [...f[field], value] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/strangers/${id}/profile`, form);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="font-medium text-midnight-black/60" data-testid="stranger-detail-loading">Loading...</p>;
  const { stranger, bookings, reviews, activity_logs } = data;

  return (
    <div data-testid="stranger-detail-page" className="max-w-4xl">
      <button onClick={() => navigate("/admin/strangers")} data-testid="back-to-strangers" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Strangers
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          {stranger.profile_photo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile-photo/${stranger.id}`}
              alt={`${stranger.first_name} ${stranger.last_name}`}
              className="w-16 h-16 rounded-2xl object-cover border border-soft-gray shrink-0"
              data-testid="stranger-profile-photo"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-soft-gray flex items-center justify-center font-extrabold text-lg shrink-0" data-testid="no-stranger-profile-photo">
              {(stranger.first_name || "?")[0]}
            </div>
          )}
          <div>
            <h2 className="font-extrabold uppercase text-2xl">{stranger.first_name} {stranger.last_name}</h2>
            <p className="text-sm font-medium text-midnight-black/60">{stranger.email} · {stranger.age ? `${stranger.age} years old` : "Age not set"}</p>
            {stranger.profile_photo_url && (
              <span className="inline-block mt-1" data-testid="stranger-photo-status"><StatusBadge status={stranger.photo_status || "pending"} /></span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={stranger.status} testId="stranger-detail-status" />
          {!editing && (
            <Button variant="outline" data-testid="stranger-edit-button" className="rounded-full font-bold text-xs" onClick={startEditing}>
              EDIT PROFILE
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-8 mb-10" data-testid="stranger-edit-form">
          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Account Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="edit-first-name-input" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input data-testid="edit-last-name-input" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <Input data-testid="edit-phone-input" placeholder="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="edit-dob-input" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
              <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="edit-gender-select"><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="woman">Woman</SelectItem>
                  <SelectItem value="man">Man</SelectItem>
                  <SelectItem value="non-binary">Non-binary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Session Preferences</h3>
            <label className="block text-xs font-bold mb-2">Interested in</label>
            <div className="grid grid-cols-3 gap-2 mb-4 max-w-sm">
              {["romantic", "friendship", "creative"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`edit-preference-${t}`}
                  onClick={() => setForm({ ...form, session_preference: t })}
                  className={`py-2 rounded-xl border-2 font-bold text-xs uppercase ${form.session_preference === t ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold mb-2">Comfortable being paired with</label>
            <div className="flex flex-wrap gap-2">
              {PAIRING_OPTIONS.map((o) => (
                <button
                  key={o}
                  type="button"
                  data-testid={`edit-pairing-${o}`}
                  onClick={() => toggleListValue("pairing_comfort", o)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase ${form.pairing_comfort.includes(o) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {o.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Personality &amp; Interests</h3>
            <div className="grid grid-cols-3 gap-2 mb-4 max-w-sm">
              {["introvert", "balanced", "extrovert"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`edit-personality-${p}`}
                  onClick={() => setForm({ ...form, personality: p })}
                  className={`py-2 rounded-xl border-2 font-bold text-xs uppercase ${form.personality === p ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold mb-2">Interests</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {INTERESTS.map((i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`edit-interest-${i.toLowerCase()}`}
                  onClick={() => toggleListValue("interests", i)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase ${form.interests.includes(i) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {i}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold mb-2">Photography experience</label>
            <div className="grid grid-cols-3 gap-2 max-w-sm">
              {["none", "some", "a-lot"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`edit-photo-experience-${p}`}
                  onClick={() => setForm({ ...form, photography_experience: p })}
                  className={`py-2 rounded-xl border-2 font-bold text-xs uppercase ${form.photography_experience === p ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Physical Posing Boundaries</h3>
            <p className="text-xs text-midnight-black/50 mb-2">These are defaults, not guarantees. The Stranger may withdraw consent at any time.</p>
            {POSING_KEYS.map((p) => (
              <ConsentToggleRow
                key={p.key}
                testId={`edit-boundary-${p.key.replace(/_/g, "-")}`}
                label={p.label}
                checked={!!form.posing_boundaries[p.key]}
                onChange={(v) => setForm({ ...form, posing_boundaries: { ...form.posing_boundaries, [p.key]: v } })}
              />
            ))}
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Comfort &amp; Safety</h3>
            <div className="space-y-3">
              <Textarea data-testid="edit-comfort-level-input" placeholder="Overall comfort level" value={form.comfort_level || ""} onChange={(e) => setForm({ ...form, comfort_level: e.target.value })} />
              <Textarea data-testid="edit-accessibility-input" placeholder="Accessibility considerations" value={form.accessibility_notes || ""} onChange={(e) => setForm({ ...form, accessibility_notes: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="edit-emergency-name-input" placeholder="Emergency contact name" value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
                <Input data-testid="edit-emergency-phone-input" placeholder="Emergency contact phone" value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
              </div>
              <Textarea data-testid="edit-expectations-input" placeholder="Session expectations" value={form.expectations_notes || ""} onChange={(e) => setForm({ ...form, expectations_notes: e.target.value })} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button data-testid="save-stranger-profile-button" disabled={saving} onClick={save} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
              {saving ? "Saving..." : "SAVE CHANGES"}
            </Button>
            <Button variant="outline" data-testid="cancel-edit-stranger-button" onClick={() => setEditing(false)} className="rounded-full font-bold">
              CANCEL
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-8 mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Account Information</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Phone</dt><dd data-testid="view-phone">{stranger.phone || "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Date of Birth</dt><dd data-testid="view-dob">{stranger.date_of_birth ? formatDate(stranger.date_of_birth) : "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Gender</dt><dd data-testid="view-gender">{stranger.gender || "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Joined</dt><dd>{formatDate(stranger.created_at)}</dd></div>
              </dl>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Session Preferences</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Interested In</dt><dd data-testid="view-session-preference">{stranger.session_preference || "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Pairing Comfort</dt><dd className="text-right">{stranger.pairing_comfort?.length ? stranger.pairing_comfort.join(", ") : "Not set"}</dd></div>
              </dl>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Personality &amp; Interests</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Personality</dt><dd>{stranger.personality || "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Interests</dt><dd className="text-right">{stranger.interests?.length ? stranger.interests.join(", ") : "Not set"}</dd></div>
                <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Photography Exp.</dt><dd>{stranger.photography_experience || "Not set"}</dd></div>
              </dl>
            </div>
            <div>
              <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Physical Posing Boundaries</h3>
              <p className="text-xs text-midnight-black/50 mb-2" data-testid="posing-boundaries-disclaimer">Defaults only, not guarantees — may be withdrawn at any time.</p>
              <div className="flex flex-wrap gap-2" data-testid="view-posing-boundaries">
                {POSING_KEYS.filter((p) => stranger.posing_boundaries?.[p.key]).map((p) => (
                  <span key={p.key} className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-midnight-black text-clean-white">{p.label}</span>
                ))}
                {POSING_KEYS.every((p) => !stranger.posing_boundaries?.[p.key]) && <span className="text-sm text-midnight-black/50">No physical contact</span>}
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Comfort &amp; Safety</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-soft-gray pb-2 gap-4"><dt className="font-bold shrink-0">Comfort Level</dt><dd className="text-right">{stranger.comfort_level || "Not set"}</dd></div>
              <div className="flex justify-between border-b border-soft-gray pb-2 gap-4"><dt className="font-bold shrink-0">Accessibility Notes</dt><dd className="text-right">{stranger.accessibility_notes || "Not set"}</dd></div>
              <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Emergency Contact</dt><dd className="text-right">{stranger.emergency_contact_name ? `${stranger.emergency_contact_name} · ${stranger.emergency_contact_phone || "no phone"}` : "Not set"}</dd></div>
              <div className="flex justify-between border-b border-soft-gray pb-2 gap-4"><dt className="font-bold shrink-0">Expectations</dt><dd className="text-right">{stranger.expectations_notes || "Not set"}</dd></div>
            </dl>
          </div>
        </div>
      )}

      {!editing && (
        <>
          <div className="mb-10">
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Bookings &amp; Reviews</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{bookings.length}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Bookings</p></div>
              <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{reviews.length}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Reviews</p></div>
            </div>
            {bookings.length > 0 && (
              <div className="space-y-2" data-testid="stranger-bookings-list">
                {bookings.map((b) => (
                  <div key={b.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                    <span>{b.session_title} · {formatDate(b.session_date)}</span>
                    <StatusBadge status={b.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Audit Trail (This Account)</h3>
            {activity_logs.length === 0 ? (
              <p className="text-sm text-midnight-black/50" data-testid="no-stranger-activity-logs">No recorded admin activity for this account.</p>
            ) : (
              <div className="space-y-2" data-testid="stranger-activity-logs">
                {activity_logs.map((log) => (
                  <div key={log.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                    <span>{log.admin_name} — {log.action.replace(/_/g, " ")}</span>
                    <span className="text-midnight-black/40 text-xs">{formatDateTime(log.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
